import { useState } from "react";
import { FormControlLabel, Stack, Switch, TextField } from "@mui/material";
import { OTP_V2_VERIFY } from "../../../../config/urls";
import { useLastSandboxResult, useOtpApi } from "../../otpApi";
import OtpTestApi from "./OtpTestApi";
import {
  END_FAILED,
  END_VERIFIED,
  HelperText,
  MappingRow,
  OutputsTable,
  ReadOnlyField,
  SectionTitle,
  SubSection,
} from "./OtpPanelParts";

const OUTPUTS = [
  { response: "200", detail: "OTP verified", route: END_VERIFIED },
  { response: "400 invalid_code", detail: "Wrong code; attempts left saved to {{attempts_left}}", route: "Ask again" },
  { response: "400 expired", detail: "The code has expired", route: "Resend OTP" },
  { response: "Resend tapped", detail: "Customer taps the Resend button", route: "Resend OTP" },
  { response: "400 max_attempts", detail: "No attempts left", route: END_FAILED },
  { response: "400 already_verified / not_found", detail: "Nothing left to verify", route: END_FAILED },
];

const OtpVerifyPreset = ({ config, handleConfigChange }) => {
  const callApi = useOtpApi();
  const lastSandbox = useLastSandboxResult();
  const [testCode, setTestCode] = useState("");

  // Default to the code the last Send test returned, so one click shows the success path
  const codeToTest = testCode || lastSandbox?.otp_code || "";

  const runTest = () =>
    callApi(OTP_V2_VERIFY, {
      body: { otp_ref_id: lastSandbox.otp_ref_id, otp_code: codeToTest },
    });

  return (
    <Stack spacing={3}>
      <SectionTitle>API</SectionTitle>
      <SubSection>
        <ReadOnlyField label="Endpoint" value="POST {BASE_HOST_URL}/api/v2/otp/verify/" />
        <ReadOnlyField label="Connection" value="OTP API Connection" />
      </SubSection>

      <SectionTitle>Ask for the Code</SectionTitle>
      <SubSection>
      <TextField
        required
        multiline
        rows={2}
        label="Prompt Text"
        value={config.prompt_text ?? "Enter the 6-digit code sent to {{masked_number}}."}
        onChange={(event) => handleConfigChange("prompt_text", event.target.value)}
        helperText="Replies that aren't exactly 6 digits are asked again without calling the API, so they don't use an attempt."
      />
      <FormControlLabel
        control={
          <Switch
            checked={config.show_resend_button ?? true}
            onChange={(event) => handleConfigChange("show_resend_button", event.target.checked)}
          />
        }
        label="Show a Resend code button"
      />
      {(config.show_resend_button ?? true) && (
        <TextField
          size="small"
          label="Resend Button Label"
          value={config.resend_button_title ?? "Resend code"}
          onChange={(event) => handleConfigChange("resend_button_title", event.target.value)}
          inputProps={{ maxLength: 20 }}
          helperText="WhatsApp allows up to 20 characters."
        />
      )}
      </SubSection>

      <SectionTitle>Request Body</SectionTitle>
      <SubSection>
        <MappingRow name="otp_ref_id">
          <TextField
            size="small"
            label="Value"
            value={config.otp_ref_id ?? "{{otp_ref_id}}"}
            onChange={(event) => handleConfigChange("otp_ref_id", event.target.value)}
          />
        </MappingRow>
        <MappingRow name="otp_code">
          <ReadOnlyField label="Value" value="The customer's reply" />
        </MappingRow>
        <HelperText>
          The code is used for the check only. It is never saved in the flow's variables.
        </HelperText>
      </SubSection>

      <SectionTitle>Outputs</SectionTitle>
      <OutputsTable rows={OUTPUTS} />
      <HelperText>Replies are routed on the API's error code, not its message text.</HelperText>

      <SectionTitle>Messages to Customer</SectionTitle>
      <SubSection>
      <TextField
        size="small"
        label="Wrong Code"
        value={config.wrong_code_message ?? "That code didn't match. {{attempts_left}} attempts left."}
        onChange={(event) => handleConfigChange("wrong_code_message", event.target.value)}
      />
      <TextField
        size="small"
        label="Code Expired"
        value={config.expired_message ?? "This code has expired. Sending a new one."}
        onChange={(event) => handleConfigChange("expired_message", event.target.value)}
      />
      <TextField
        size="small"
        label="Max Attempts"
        value={config.max_attempts_message ?? "Too many wrong attempts. Please start again."}
        onChange={(event) => handleConfigChange("max_attempts_message", event.target.value)}
      />
      </SubSection>

      <OtpTestApi
        onRun={runTest}
        disabled={!lastSandbox || codeToTest.length !== 6}
        disabledReason="Run Test API on Send OTP first, then enter a 6-digit code."
      >
        <TextField
          size="small"
          label="Test code"
          value={codeToTest}
          onChange={(event) => setTestCode(event.target.value.trim())}
          helperText="Pre-filled with the code from the last Send OTP test. Type a different one to see the wrong-code response. After a successful verify, run the Send test again."
        />
      </OtpTestApi>
    </Stack>
  );
};

export default OtpVerifyPreset;
